import { useEffect, useState } from 'react';
import { Table, Button, Empty, Tag, Tooltip, Space } from 'antd';
import { EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { internalAccounts } from '../../api/internalAdminEndpoints';
import { useAppNotifications } from '../../utils/notify';
import LoadingBlock from '../../components/LoadingBlock';
import PageHeader from '../../components/PageHeader';
import { formatDateTime } from '../../utils/format';
import { CompanyCell, ContactCell } from '../../components/internalAdmin/AccountCells';

// Stage 5N-2C: rejected-accounts page. Read-only — there's no
// transition out of 'rejected' from this surface (the customer must
// complete a fresh signup).

export default function RejectedAccountsPage() {
  const { notifyError } = useAppNotifications();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ items: [], total: 0 });

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const resp = await internalAccounts.list({ status: 'rejected', limit: 50 });
      setData({
        items: Array.isArray(resp.items) ? resp.items : [],
        total: typeof resp.total === 'number' ? resp.total : 0
      });
    } catch (err) {
      setError(err);
      notifyError(err, { title: 'Could not load rejected accounts' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const columns = [
    { title: 'Company', key: 'company', width: 220, ellipsis: true,
      render: (_v, row) => <CompanyCell row={row} /> },
    { title: 'GSTIN', dataIndex: 'gstin', key: 'gstin', width: 150,
      render: (v) => v ? <code className="text-[11px] text-ink-700">{v}</code> : <span className="text-ink-400">—</span> },
    { title: 'Contact', key: 'contact', width: 220, ellipsis: true,
      render: (_v, row) => <ContactCell row={row} /> },
    { title: 'Rejected', key: 'rejectedAt', width: 160,
      render: (_v, row) => <span className="text-xs text-ink-600">{formatDateTime(row.rejectedAt || row.createdAt)}</span> },
    { title: 'Reason', key: 'rejectReason',
      render: (_v, row) => row.rejectReason ? (
        <Tooltip title={row.rejectReason} mouseEnterDelay={0.3}>
          <span className="text-xs text-ink-700 line-clamp-2">{row.rejectReason}</span>
        </Tooltip>
      ) : <span className="text-ink-400">—</span> },
    { title: 'Actions', key: 'actions', width: 100, fixed: 'right',
      render: (_v, row) => (
        <Button size="small" icon={<EyeOutlined />}
          onClick={() => navigate(`/internal/accounts/${row.tenantId}`)}>
          View
        </Button>
      )
    }
  ];

  return (
    <div>
      <PageHeader
        title="Rejected Accounts"
        subtitle="Signups that did not pass internal review. The customer must start a fresh signup to recover."
        actions={
          <Space>
            <Tag className="!text-sm !px-3 !py-1">{data.total} rejected</Tag>
            <Button icon={<ReloadOutlined />} onClick={load} disabled={loading}>Refresh</Button>
          </Space>
        }
      />

      {loading ? <LoadingBlock tip="Loading rejected accounts…" /> : (
        <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
          <Table
            rowKey="tenantId"
            dataSource={data.items}
            columns={columns}
            size="middle"
            pagination={false}
            scroll={{ x: 1100 }}
            tableLayout="fixed"
            locale={{ emptyText: error
              ? <div className="py-12 text-center text-ink-500 text-sm">Could not load rejected accounts.</div>
              : <div className="py-10">
                  <Empty
                    description={
                      <div className="text-ink-700">
                        <p className="text-sm font-semibold m-0">No rejected accounts</p>
                        <p className="text-xs text-ink-500 m-0 mt-1">
                          Rejected signups will appear here with their reason.
                        </p>
                      </div>
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                </div>
            }}
          />
        </div>
      )}
    </div>
  );
}
